import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ContextualMenu, SearchBox, DirectionalHint, Stack } from '@bingads-webui-react/fabric-control';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getResponseErrors, publisherQueries as defaultAppQueries } from '@bingads-webui-campaign-react/merchant-shared';
import { Loading } from '@bingads-webui-campaign-react/loading';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { debounce } from 'underscore';
import { Button } from 'react-bootstrap/lib/InputGroup';
import { Text } from '@fluentui/react';
import { getStyle } from './store-app-search-box-style';

const MAX_SEARCH_KEYWORD_LENGTH = 200;
const MIN_SEARCH_KEYWORD_LENGTH = 2;

function UnstyledStoreAppSelector({
  i18n,
  classes,
  appQueries,
  customerId,
  campaignId,
  isPanelOpen,
  preSelectedApp,
  updateValidation,
}) {
  const linkRef = useRef(null);
  const [showContextualMenu, setShowContextualMenu] = useState(false);
  const onShowContextualMenu = useCallback(() => setShowContextualMenu(true), []);
  const onHideContextualMenu = useCallback(() => setShowContextualMenu(false), []);
  const [menuItems, setMenuItems] = useState([]);
  const [isNoMatchedApps, setIsNoMatchedApps] = useState(false);
  const [searchError, setSearchError] = useState();
  const [selectedApp, setSelectedApp] = useState(preSelectedApp);

  const [searchKeyword, setSearchKeyword] = useState('');
  const [validateError, setValidateError] = useState();

  const resetWarningsAndErrors = () => {
    setValidateError(null);
    setSearchError(null);
    setIsNoMatchedApps(false);
  };

  useEffect(() => {
    setSearchKeyword('');
    resetWarningsAndErrors();
  }, [isPanelOpen]);

  useEffect(() => {
    setSelectedApp(preSelectedApp);
  }, [preSelectedApp]);

  useEffect(() => {
    if (selectedApp && selectedApp.isPreselected) {
      return;
    }
    if (selectedApp == null && preSelectedApp != null) {
      return;
    }
    updateValidation(selectedApp ? [] : ['Please select one store app'], selectedApp);
  }, [preSelectedApp, selectedApp, updateValidation]);

  const onRenderAppItem = (dismissMenu, app) => (
    <Stack
      onClick={() => {
          setSearchKeyword('');
          dismissMenu();
          setSelectedApp(app);
        }}
      horizontal
      className={classNames('matched-app-item')}
    >
      {/* <Image src={app.ImageLink} className="app-id-logo" /> */}
      <Stack.Item className="app-id-col">
        <Stack.Item className={classNames(classes.textEntry, 'app-id-apptitle')} title={app.AppTitle}>{app.AppTitle}</Stack.Item>
        <Stack.Item className={classNames(classes.textEntry, 'app-id-publisher')} title={app.PublisherName}>{app.PublisherName}</Stack.Item>
      </Stack.Item>
    </Stack>
  );

  const onSearchMatchedApps = useCallback(debounce((searchText) => {
    // eslint-disable-next-line no-param-reassign
    searchText = searchText ? searchText.trim() : '';
    if (searchText.length === 0) {
      return;
    }

    if (searchText.length > MAX_SEARCH_KEYWORD_LENGTH) {
      onHideContextualMenu();
      setValidateError(i18n.getString(_TL_('Field should not exceed {{maxSearchKeywordLength}} characters'), {
        maxSearchKeywordLength: MAX_SEARCH_KEYWORD_LENGTH,
      }));
      return;
    }

    if (searchText.length < MIN_SEARCH_KEYWORD_LENGTH) {
      onHideContextualMenu();
      setValidateError(i18n.getString(_TL_('Please enter at least {{minSearchKeywordLength}} characters.'), {
        minSearchKeywordLength: MIN_SEARCH_KEYWORD_LENGTH,
      }));
      return;
    }
    setValidateError(null);
    setMenuItems([{
      key: 'loading',
      onRender: () => (
        <div className={classes.searchAppLoading}>
          <Loading isVisible text={i18n.getString(_TL_('Loading'))} />
        </div>
      ),
    }]);
    onShowContextualMenu();
    appQueries.getMatchedPublishers(customerId, searchText, 'Application')
      .then((response) => {
        if (response && ((response.MatchingApps && response.MatchingApps.length > 0) ||
        (response.MatchingAppications && response.MatchingAppications.length > 0))) {
          const items = response.MatchingAppications;
          const newMenuItems = items.map(app => ({
            key: app.AppId,
            text: app.AppName,
            onRender: (_item, dismissMenu) => onRenderAppItem(dismissMenu, app),
          }));
          setMenuItems(newMenuItems);
          setIsNoMatchedApps(false);
        } else {
          setMenuItems([]);
          onHideContextualMenu();
          setIsNoMatchedApps(true);
        }
      })
      .catch((response) => {
        const error = getResponseErrors(response, i18n);
        setSearchError(error[0].LocalizedMessage);
        setMenuItems([]);
        onHideContextualMenu();
      });
  }, 500, false), [classes.searchAppLoading, customerId, i18n, onHideContextualMenu, onRenderAppItem, onShowContextualMenu, appQueries, validateError]);

  return selectedApp ?
    (
      <>
        <Text className={classes.searchPromptText}>{i18n.getString(_TL_('You’ve selected to advertise the following app:'))}</Text>
        <Stack className={classNames(classes.selectedStoreApp, 'selected-store-app')}>
          {/* <Image className={classes.selectedStoreAppImage} src={selectedApp.ImageLink} /> */}
          <Stack.Item className="text-wrapper">
            <Stack.Item className={classNames(classes.textEntry)}><b>{i18n.getString(_TL_('App name: '))}</b><Text className="selected-app-name">{selectedApp.AppTitle}</Text></Stack.Item>
            <Stack.Item className={classNames(classes.textEntry)}><b>{i18n.getString(_TL_('Publisher: '))}</b><Text className="selected-publisher">{selectedApp.PublisherName}</Text></Stack.Item>
            <Stack.Item className={classNames(classes.textEntry)}><b>{i18n.getString(_TL_('App ID: '))}</b><Text className="selected-app-id">{selectedApp.AppId}</Text></Stack.Item>
          </Stack.Item>
          {
            !campaignId && (
              <Stack.Item className="close-button">
                <Button
                  className={classNames(classes.selectedStoreAppDismiss, 'iconba iconba-Cancel')}
                  onClick={() => {
                  setSelectedApp(null);
                }}
                />
              </Stack.Item>
            )
          }
        </Stack>
      </>
    ) :
    (
      <>
        <Text className={classes.searchPromptText}>{i18n.getString(_TL_('Search for the Store app you’d like to advertise.'))}</Text>
        <SearchBox
          id="search_app"
          ref={linkRef}
          onSearch={onSearchMatchedApps}
          placeholder={i18n.getString(_TL_('Search by app name'))}
          onClear={() => {
            setSearchKeyword('');
            resetWarningsAndErrors();
          }}
          value={searchKeyword}
          onChange={(ev, newValue) => {
            const text = newValue || '';
            setSearchKeyword(text);
            onSearchMatchedApps(text);
          }}
        />
        <div className={classes.searchHintWrap}>
          {/* <p className={classes.searchHint}>
            {i18n.getString(_TL_('Search by App name, or Store ID'))}
          </p> */}
          <p className={classNames('search-keyword-letter-count', classes.searchHint)}>
            {searchKeyword.length > MAX_SEARCH_KEYWORD_LENGTH ? i18n.getString(_TL_('Exceeded by {{exceededCount}} characters'), {
              exceededCount: searchKeyword.length - MAX_SEARCH_KEYWORD_LENGTH,
            }) : i18n.getString(_TL_('{{leftCount}} characters remaining'), {
              leftCount: MAX_SEARCH_KEYWORD_LENGTH - searchKeyword.length,
            })}
          </p>
        </div>
        {isNoMatchedApps && <p>{i18n.getString(_TL_('No matched apps found.'))}</p>}
        {searchError && <p className="search-error">{searchError}</p>}
        <ContextualMenu
          target={linkRef}
          items={menuItems}
          hidden={!showContextualMenu}
          onDismiss={onHideContextualMenu}
          directionalHint={DirectionalHint.bottomLeftEdge}
          className={classes.searchAppContextualMenu}
        />
        {validateError && <p className="validate-error">{validateError}</p>}
      </>
    );
}

UnstyledStoreAppSelector.propTypes = {
  classes: PropTypes.shape({
    searchAppContextualMenu: PropTypes.string.isRequired,
    searchHint: PropTypes.string.isRequired,
    searchAppLoading: PropTypes.string.isRequired,
    searchHintWrap: PropTypes.string.isRequired,
    selectedStoreApp: PropTypes.string.isRequired,
    selectedStoreAppImage: PropTypes.string.isRequired,
    selectedStoreAppDismiss: PropTypes.string.isRequired,
    textEntry: PropTypes.string.isRequired,
    searchPromptText: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  appQueries: PropTypes.shape({
    getMatchedPublishers: PropTypes.func.isRequired,
  }),
  customerId: PropTypes.number.isRequired,
  campaignId: PropTypes.number,
  isPanelOpen: PropTypes.bool.isRequired,
  preSelectedApp: PropTypes.shape({}),
  updateValidation: PropTypes.func.isRequired,
};

UnstyledStoreAppSelector.defaultProps = {
  appQueries: defaultAppQueries,
  preSelectedApp: null,
  campaignId: null,
};

export const StoreAppSelector = withCustomValidation(withDefaultStyles(UnstyledStoreAppSelector, getStyle));
